// @greenlight-spec: 09-data-brokering.md
// @greenlight-spec: 10-security-compliance.md
// @greenlight-spec: 16-api-conventions.md
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { PaginationQuerySchema, PermissionListItemSchema, PermissionSchema, PermissionStatusSchema, paginated, } from '@greenlight/shared';
import { AppIdParamsSchema, errorResponses } from './common.js';
const c = initContract();
const PermIdParamsSchema = AppIdParamsSchema.extend({ permId: z.string().uuid() });
const GrantIdParamsSchema = z.object({ grantId: z.string().uuid() });
export const ReviewPermissionSchema = z.object({
    reason: z.string().max(2000).optional(),
});
// Cross-app queue filters: pagination + optional status / integration narrowing.
// Query keys are camelCase, matching the other list-filter contracts
// (audit `appId`/`actorUserId`, knowledge `integrationId`). The response
// projection stays snake_case like the other read shapes.
const PermissionQueueQuerySchema = PaginationQuerySchema.extend({
    status: PermissionStatusSchema.optional(),
    integrationId: z.string().uuid().optional(),
    // With integrationId, keys the credential-centric "who has access" ledger
    // read: every principal (app + user) against one credential (ADR-0029).
    credentialSlug: z.string().optional(),
});
export const permissionsContract = c.router({
    queue: {
        method: 'GET',
        path: '/api/permissions',
        query: PermissionQueueQuerySchema,
        responses: {
            200: paginated(PermissionListItemSchema),
            ...errorResponses,
        },
        summary: 'Cross-app permission review queue (org-wide).',
    },
    list: {
        method: 'GET',
        path: '/api/apps/:id/permissions',
        pathParams: AppIdParamsSchema,
        query: PaginationQuerySchema,
        responses: {
            200: paginated(PermissionSchema),
            ...errorResponses,
        },
        summary: 'List permission grants/requests for an app.',
    },
    approve: {
        method: 'POST',
        path: '/api/apps/:id/permissions/:permId/approve',
        pathParams: PermIdParamsSchema,
        body: ReviewPermissionSchema,
        responses: { 200: PermissionSchema, ...errorResponses },
        summary: 'Approve a pending permission request.',
    },
    deny: {
        method: 'POST',
        path: '/api/apps/:id/permissions/:permId/deny',
        pathParams: PermIdParamsSchema,
        body: ReviewPermissionSchema,
        responses: { 200: PermissionSchema, ...errorResponses },
        summary: 'Deny a pending permission request.',
    },
    revoke: {
        method: 'POST',
        path: '/api/apps/:id/permissions/:permId/revoke',
        pathParams: PermIdParamsSchema,
        body: ReviewPermissionSchema,
        responses: { 200: PermissionSchema, ...errorResponses },
        summary: 'Revoke a previously granted permission.',
    },
    // Principal-agnostic review keyed on the grant id alone — the only reachable
    // path for a user grant, which has no app to key on (ADR-0029).
    approveById: {
        method: 'POST',
        path: '/api/credential-grants/:grantId/approve',
        pathParams: GrantIdParamsSchema,
        body: ReviewPermissionSchema,
        responses: { 200: PermissionSchema, ...errorResponses },
        summary: 'Approve a pending credential grant (any principal).',
    },
    denyById: {
        method: 'POST',
        path: '/api/credential-grants/:grantId/deny',
        pathParams: GrantIdParamsSchema,
        body: ReviewPermissionSchema,
        responses: { 200: PermissionSchema, ...errorResponses },
        summary: 'Deny a pending credential grant (any principal).',
    },
    revokeById: {
        method: 'POST',
        path: '/api/credential-grants/:grantId/revoke',
        pathParams: GrantIdParamsSchema,
        body: ReviewPermissionSchema,
        responses: { 200: PermissionSchema, ...errorResponses },
        summary: 'Revoke a granted credential grant (any principal).',
    },
});
//# sourceMappingURL=permissions.js.map